import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { LineChart, Line, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { Pulse } from "@phosphor-icons/react";

export default function Symptoms() {
  const [items, setItems] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    log_date: today, mood: "", sleep_hours: "", energy: 3,
    weight_kg: "", bp_systolic: "", bp_diastolic: "", blood_sugar: "",
    nausea: 0, pain: 0, kick_count: "", notes: "",
  });
  const [busy, setBusy] = useState(false);

  const load = async () => { const r = await api.get("/health/symptoms?days=30"); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form };
    ["sleep_hours", "weight_kg", "bp_systolic", "bp_diastolic", "blood_sugar", "kick_count", "energy", "nausea", "pain"].forEach((k) => {
      payload[k] = payload[k] === "" ? null : Number(payload[k]);
    });
    try {
      await api.post("/health/symptoms", payload);
      toast.success("Logged for today.");
      load();
    } catch { toast.error("Could not save."); }
    finally { setBusy(false); }
  };

  const chart = [...items].reverse().map((i) => ({
    date: i.log_date.slice(5),
    weight: i.weight_kg,
    sleep: i.sleep_hours,
    bp: i.bp_systolic,
    mood: ({ great: 5, good: 4, okay: 3, low: 2, anxious: 1 })[i.mood] || null,
  }));

  return (
    <div className="space-y-8" data-testid="symptoms-page">
      <header>
        <p className="overline text-saffron mb-2">Wellness</p>
        <h1 className="font-serif font-semibold text-4xl sm:text-5xl">Symptoms & wellness</h1>
        <p className="text-ink-700 mt-2">A daily pulse-check. Patterns become wisdom.</p>
      </header>

      <form onSubmit={submit} className="vansha-card p-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="symptom-form">
        <div><label className="overline mb-2 block">Date</label><input type="date" required value={form.log_date} onChange={(e) => setForm({ ...form, log_date: e.target.value })} className="input-line" data-testid="s-date" /></div>
        <div><label className="overline mb-2 block">Mood</label>
          <select value={form.mood} onChange={(e) => setForm({ ...form, mood: e.target.value })} className="input-line bg-transparent" data-testid="s-mood">
            <option value="">—</option><option value="great">Great</option><option value="good">Good</option><option value="okay">Okay</option><option value="low">Low</option><option value="anxious">Anxious</option>
          </select>
        </div>
        <div><label className="overline mb-2 block">Sleep (hrs)</label><input type="number" step="0.5" value={form.sleep_hours} onChange={(e) => setForm({ ...form, sleep_hours: e.target.value })} className="input-line" data-testid="s-sleep" /></div>
        <div><label className="overline mb-2 block">Energy (1-5)</label><input type="number" min="1" max="5" value={form.energy} onChange={(e) => setForm({ ...form, energy: e.target.value })} className="input-line" data-testid="s-energy" /></div>
        <div><label className="overline mb-2 block">Weight (kg)</label><input type="number" step="0.1" value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} className="input-line" data-testid="s-weight" /></div>
        <div><label className="overline mb-2 block">BP (mmHg)</label>
          <div className="flex gap-2">
            <input type="number" placeholder="120" value={form.bp_systolic} onChange={(e) => setForm({ ...form, bp_systolic: e.target.value })} className="input-line" data-testid="s-bp-sys" />
            <input type="number" placeholder="80" value={form.bp_diastolic} onChange={(e) => setForm({ ...form, bp_diastolic: e.target.value })} className="input-line" data-testid="s-bp-dia" />
          </div>
        </div>
        <div><label className="overline mb-2 block">Nausea (0-5)</label><input type="number" min="0" max="5" value={form.nausea} onChange={(e) => setForm({ ...form, nausea: e.target.value })} className="input-line" data-testid="s-nausea" /></div>
        <div><label className="overline mb-2 block">Kick count</label><input type="number" value={form.kick_count} onChange={(e) => setForm({ ...form, kick_count: e.target.value })} className="input-line" data-testid="s-kicks" /></div>
        <div className="sm:col-span-2 lg:col-span-4"><label className="overline mb-2 block">Notes</label><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-line" rows="2" data-testid="s-notes" /></div>
        <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
          <button type="submit" disabled={busy} className="btn-primary" data-testid="s-submit">{busy ? "Saving…" : "Log today"}</button>
        </div>
      </form>

      {chart.length > 1 && (
        <div className="grid lg:grid-cols-2 gap-6" data-testid="trend-charts">
          <ChartCard title="Weight trend" data={chart} dataKey="weight" color="#9c4028" />
          <ChartCard title="Sleep trend" data={chart} dataKey="sleep" color="#2b4736" />
        </div>
      )}

      <section>
        <h2 className="font-serif text-2xl mb-4">Recent entries</h2>
        {items.length === 0 ? <p className="text-ink-500" data-testid="symptoms-empty">No entries yet.</p> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.slice(0, 9).map((i) => (
              <div key={i.id || i.log_date} className="vansha-card p-5" data-testid={`s-entry-${i.log_date}`}>
                <p className="overline text-saffron">{i.log_date}</p>
                <div className="grid grid-cols-2 gap-2 mt-3 text-sm text-ink-700">
                  {i.mood && <span>Mood: <b>{i.mood}</b></span>}
                  {i.sleep_hours && <span>Sleep: <b>{i.sleep_hours}h</b></span>}
                  {i.weight_kg && <span>Weight: <b>{i.weight_kg}kg</b></span>}
                  {i.bp_systolic && <span>BP: <b>{i.bp_systolic}/{i.bp_diastolic}</b></span>}
                  {i.kick_count != null && <span>Kicks: <b>{i.kick_count}</b></span>}
                  {i.nausea != null && <span>Nausea: <b>{i.nausea}/5</b></span>}
                </div>
                {i.notes && <p className="text-sm text-ink-500 mt-2 italic">{i.notes}</p>}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function ChartCard({ title, data, dataKey, color }) {
  return (
    <div className="vansha-card p-6">
      <h3 className="font-serif text-xl mb-4">{title}</h3>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={data}>
          <CartesianGrid stroke="#eae5df" strokeDasharray="3 3" />
          <XAxis dataKey="date" stroke="#7a827c" fontSize={12} />
          <YAxis stroke="#7a827c" fontSize={12} />
          <RTooltip contentStyle={{ background: "#fdfcf9", border: "1px solid #eae5df", borderRadius: "12px" }} />
          <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={{ r: 3 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
